<template>
  <div>
    <v-row justify="space-between" v-if="search">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <search-text
          :text="'Search by name'"
          :value="'text'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
          data-cy="name"
        ></search-text>
      </v-col>
      <v-col cols="auto">
        <v-btn outlined class="black--text font-weight-bold mb-2" rounded large @click="download">
          <v-icon color="black" small class="mr-2">
            mdi-download
          </v-icon>
          Download
        </v-btn>
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="items"
      class="elevation-0 wrap-headers"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      must-sort
      item-class="properties"
      :dense="false"
      :footer-props="footerProps"
    >
      <template v-slot:item.logo="{ item }">
        <v-img
          class="my-2"
          style="border-radius: 8px;"
          height="75"
          width="65"
          :src="item.logoUrlPublic ? item.logoUrlPublic : item.logoUrl"
          contain
        >
          <template v-slot:placeholder>
            <v-img style="border-radius: 8px;" lazy-src="@/assets/img/blur.jpg"></v-img>
          </template>
        </v-img>
      </template>
      <template v-slot:item.name="{ item }">
        <div class="font-weight-bold">
          {{ item.name }}
        </div>
      </template>
      <template v-slot:item.totalViews="{ item }">
        <div class="font-weight-bold" tyle="border-left: 2px solid black !important;">
          {{ item.totalViews }} views
        </div>
      </template>
      <template v-slot:item.views="{ item }">
        <div class="font-weight-bold" tyle="border-left: 2px solid black !important;">
          {{ item.views }} views
        </div>
      </template>
      <template v-slot:item.viewsNonRegistered="{ item }">
        <div class="font-weight-bold" tyle="border-left: 2px solid black !important;">
          {{ item.viewsNonRegistered }} views
        </div>
      </template>
      <template v-slot:item.requested="{ item }">
        <div class="font-weight-bold" tyle="border-left: 2px solid black !important;">
          {{ item.requested }} orders
        </div>
      </template>
      <template v-slot:item.accepted="{ item }">
        <div class="font-weight-bold" tyle="border-left: 2px solid black !important;">
          {{ item.accepted }} orders
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import SearchText from '@/components/shared/search/SearchText';

export default {
  name: 'TableStatistics',
  mixins: [util],
  components: {
    SearchText,
  },
  props: {
    urlApi: String,
    search: Object,
    searchAttributes: Array,
    name: String,
  },
  data: () => ({
    firstLoad: true,
    totalItems: 0,
    items: [],
    loading: true,
    options: {},
    headers: [
      {
        text: '',
        value: 'logo',
        width: 200,
      },
      { text: 'Name', value: 'name', width: 300, sortable: true },
      { text: 'Total Views', value: 'totalViews', width: 200, sortable: true },
      { text: 'Registered Views', value: 'views', width: 200, sortable: true },
      { text: 'Non Registered Views', value: 'viewsNonRegistered', sortable: true },
      { text: 'Number Of Orders Initiated', value: 'requested', sortable: true },
      { text: 'Number Of Orders Delivered', value: 'accepted', sortable: true },
    ],
    footerProps: { 'items-per-page-options': [10, 15, 25, 50, 100] },
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.$route.query[attr]) {
            this.$set(this.search, attr, this.$route.query[attr]);
          }
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
    this.triggerClear();
  },
  computed: {
    downloadInfo() {
      const downloadMapping = {
        '/api/v1/providers/views': {
          endpoint: 'api/v1/providers/views/download',
          filename: 'providers-statistics.xlsx',
        },
        '/api/v1/resources/views': {
          endpoint: 'api/v1/resources/views/download',
          filename: 'services-statistics.xlsx',
        },
        '/api/v1/providers/views-per-institution': {
          endpoint: 'api/v1/providers/views-per-institution/download',
          filename: 'hosting-institutions-statistics.xlsx',
        },
        '/api/v1/equipment/views': {
          endpoint: 'api/v1/equipment/views/download',
          filename: 'equipment-statistics.xlsx',
        },
        '/api/v1/patent/views': {
          endpoint: 'api/v1/patent/views/download',
          filename: 'patent-statistics.xlsx',
        },
      };
      return downloadMapping[this.urlApi] || { endpoint: '', filename: '' };
    },
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);
      return new Promise(resolve => {
        this.axios
          .get(`${this.urlApi}?`, {
            params: this.getParams(),
          })
          .then(res => {
            let items = [];
            let total = 0;
            if (res.status === 200) {
              items = res.data.content;
              total = res.data.total;
            }
            setTimeout(() => {
              this.loading = false;
              resolve({
                items,
                total,
              });
            });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }
      return query;
    },
    download() {
      const { endpoint, filename } = this.downloadInfo;
      if (!endpoint) {
        console.error('No valid download endpoint found.');
        return;
      }

      this.axios
        .get(endpoint, {
          responseType: 'blob',
        })
        .then(response => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = filename;
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });
    },
  },
};
</script>

<style>
.v-application--is-ltr .v-data-table__mobile-row__cell {
  text-align: left;
}

tr {
  min-height: 200px;
  height: 100%;
}

#table-providers
  .theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:first-child {
  width: 97px !important;
}
</style>
